import React from "react";
import {useIdleTimer} from "react-idle-timer";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import {FormButton} from "components/FormComponents/Button/FormButton";
import FormInstructions from "components/FormComponents/Typography/FormInstructions";
import {useAppSelector, useAppDispatch, store} from "../../redux/store";
import {getRequest, setHeader} from "repository/genericRepository";
import {FETCH_TOKEN} from "commonUtils/globalAPISettings";
import {clearAuthState} from "../../redux/reducers/authSlice";
import {useNavigate} from "react-router-dom";
import {clearlogs, postLogsToAPI} from "../../redux/reducers/loggerSlice";
import DialogTitleCommon from "components/Dialogs/DialogTitleCommon";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import Grid from "@mui/material/Grid";
import {
  Alerts,
  AxiosErrorCode,
  CPSConstants,
  CPSMessages,
  HttpStatusCode,
  PageNames,
} from "commonUtils/enumerations";
import {
  HttpException,
  ServiceAuthenticationException,
} from "services/exceptionHandlerService";
import FormValidationAlert from "components/FormComponents/Alert/FormValidationAlert";
import {logMessage} from "services/loggerService";
import {loggedMessage} from "commonUtils/utils";

const IdleTimerConatiner: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [open, setOpen] = React.useState(false);
  const [errorMsg, setErrorMsg] = React.useState<string>("");

  const IDLE_TIMEOUT_SECONDS = 1020;
  const COUNTDOWN_SECONDS = 179;

  const [countdown, setCountdown] = React.useState(COUNTDOWN_SECONDS);
  const countdownIntervalRef = React.useRef<number | null>(null);

  const {authDetails, userDetails} = useAppSelector(state => {
    return state.auth;
  });

  const logs = useAppSelector(state => {
    return state.logs;
  });

  const onIdle = () => {
    try {
      dispatch(postLogsToAPI({logs: logs, token: authDetails?.token}));
      dispatch(clearlogs());
      setOpen(true);
    } catch (error: any) {
      handleAxiosErrors(error);
    }
  };

  const handleSignin = () => {
    try {
      logMessage(
        "INFO",
        `User ${userDetails?.userName} has clicked the idle dialog "Yes,Keep me signed in button".`,
        PageNames.DASHBOARD,
      );
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
        countdownIntervalRef.current = null;
      }
      setOpen(false);
      setCountdown(COUNTDOWN_SECONDS);
      reset(); // Restart idle timer
      getTokenCall();
    } catch (error: any) {
      handleAxiosErrors(error);
    }
  };

  const handleSignOut = () => {
    try {
      logMessage(
        "INFO",
        `User ${userDetails?.userName} has clicked the idle dialog "No,Sign me out button".`,
        PageNames.DASHBOARD,
      );
      setOpen(false);
      setCountdown(COUNTDOWN_SECONDS);
      dispatch(clearAuthState());
      navigate(CPSConstants.UNAUTH_QUERY_STRING_SESSION);
    } catch (error: any) {
      handleAxiosErrors(error);
    }
  };

  const getTokenCall = async () => {
    try {
      const authState = store.getState();
      const APIHeaders = {
        Authorization: `Bearer ${authState.auth?.authDetails?.token}`,
      };
      const responseData = await getRequest({
        url: `${FETCH_TOKEN}`,
        headers: setHeader(APIHeaders),
        timeout: 60000,
        params: {},
      });
      if (responseData?.messageCode !== 0 && responseData?.status === false) {
        dispatch(clearAuthState());
        navigate("/");
      }
    } catch (error: any) {
      loggedMessage(error, errorMsg, PageNames.DASHBOARD);
      handleAxiosErrors(error);
    }
  };

  const handleAxiosErrors = (error: any) => {
    const data = {pagename: PageNames.DASHBOARD};
    if (error?.code === AxiosErrorCode.ERR_BAD_REQUEST) {
      if (
        error?.response?.status === HttpStatusCode.UNAUTHORIZED ||
        error?.response?.status === HttpStatusCode.FORBIDDEN
      ) {
        ServiceAuthenticationException(error, data, error?.response?.status);
      } else if (error?.response?.status === HttpStatusCode.BAD_REQUEST) {
        setErrorMsg(error.response.data.message);
      } else if (error?.response?.status === HttpStatusCode.NOT_FOUND) {
        setErrorMsg(CPSMessages.SERVICE_NOT_FOUND);
      } else if (
        error?.response?.status === HttpStatusCode.INTERNAL_SERVER_ERROR
      ) {
        HttpException(error, data);
        setErrorMsg(CPSMessages.INTERNAL_SERVER_ERROR);
      } else {
        setErrorMsg(error.message);
      }
    } else if (error?.code === AxiosErrorCode.ECONNABORTED) {
      setErrorMsg(CPSMessages.TIMEOUT_ERROR);
    } else if (error?.code === AxiosErrorCode.ERR_NETWORK) {
      setErrorMsg(CPSMessages.NETWORK_ERROR);
    } else {
      setErrorMsg(error.message);
    }
  };

  const {reset} = useIdleTimer({
    onIdle,
    timeout: IDLE_TIMEOUT_SECONDS * 1000,
    throttle: 500,
  });

  React.useEffect(() => {
    if (open) {
      setCountdown(COUNTDOWN_SECONDS);

      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
      }

      countdownIntervalRef.current = window.setInterval(() => {
        setCountdown(prev => {
          if (prev <= 1) {
            clearInterval(countdownIntervalRef.current!);
            countdownIntervalRef.current = null;
            handleSignOut();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    } else {
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
        countdownIntervalRef.current = null;
      }
      setCountdown(COUNTDOWN_SECONDS);
    }

    return () => {
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
        countdownIntervalRef.current = null;
      }
    };
  }, [open]);

  return (
    <>
      <Dialog
        open={open}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        PaperProps={{
          sx: {
            maxHeight: "25rem",
            height: "18rem",
            width: "30rem",
            padding: "0 0 10px 0",
          },
        }}>
        <DialogTitleCommon title="Confirm" />
        <DialogContent>
          {errorMsg && (
            <FormValidationAlert severity={Alerts.ERROR} message={errorMsg} />
          )}
          <DialogContentText id="alert-dialog-description">
            <br></br>
            <Grid container spacing={1}>
              <Grid item xs={1}>
                <WarningAmberIcon sx={{color: "red", fontSize: "4rem"}} />
              </Grid>
              <Grid item xs={11}>
                <FormInstructions
                  outlet={`You will be logged out in ${countdown} .`}
                />
                <br></br>
                <FormInstructions outlet="Do you want to stay signed in?" />
              </Grid>
            </Grid>
            {/* You are about to logout */}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{display: "flex", justifyContent: "center"}}>
          <FormButton
            size={"small"}
            variant="contained"
            onClick={handleSignOut}
            outlet="No, Sign me out"
          />
          <FormButton
            size={"small"}
            variant="contained"
            onClick={handleSignin}
            autoFocus
            outlet="Yes, Keep me signed in"
          />
        </DialogActions>
      </Dialog>
    </>
  );
};

export default IdleTimerConatiner;
